import { isExplicitDesiredDate } from 'src/stores/_infrastructure/desiredDateFilter';
import { useFilterStore } from 'src/stores/filter';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useTaskListStore } from 'src/stores/taskList';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { DateRangeSelection } from 'src/models/common';
import { ActivityListTypeEnum, MODULE_LIST_TYPE } from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { StatusEnum } from 'src/models/status/enums';

import { today } from 'src/util/date';
import { registerListInstanceFetcher, resolveListInstance } from 'src/util/listInstance/listInstanceFetchDispatch';
import { registerModuleTabSettingsReset } from 'src/util/listInstance/moduleTabSettingsReset';
import { QueryStore } from 'src/util/query/QueryStore';

const warmupTaskList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	}
) => {
	const taskListStore = useTaskListStore();

	void taskListStore.fetchFilterBoxes({
		queryStore: queryStore,
	});

	void taskListStore.fetchFilterUsers({
		queryStore: queryStore,
	});

	void fetchTaskList(_store, queryStore, {
		...queryParameters,
		clearResultsBeforeFetch: false,
	});
};

const fetchTaskList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		clearResultsBeforeFetch: boolean;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	},
	listStore: ReturnType<typeof useTaskListStore> = useTaskListStore()
) => {
	const hasActiveSearch = queryParameters.searchTerm.trim() !== '' || queryParameters.hiddenSearchTerm.trim() !== '';
	const didClearSubscriptionsForSearch = hasActiveSearch;

	if (isExplicitDesiredDate(queryParameters.desiredDate)) {
		listStore.setDateFilterAction({
			dateFilter: queryParameters.desiredDate,
		});
	}

	if (hasActiveSearch) {
		listStore.clearSubscriptions({
			queryStore: queryStore,
		});
	}

	if (queryParameters.clearResultsBeforeFetch) {
		if (
			queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}

			listStore.silentSetOpenTaskList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.MINE ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}

			listStore.silentSetMineTaskList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.ALL ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}

			listStore.silentSetAllTaskList();
		}
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchOpenTaskList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.MINE ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchMineTaskList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.ALL ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchAllTaskList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}
};

registerListInstanceFetcher('taskList', ({ listStore, params, queryStore, runtimeStore }) =>
	fetchTaskList(runtimeStore as never, queryStore as never, params as never, listStore as never)
);

// Task 2.1 (analog Task 1.7 / noteList): Basis-Identifier der drei Sub-Listen-Scroll-Positionen
// (Offen/Meine/Alle) — muss `TaskList.vue`s `virtual-list-id="moduleActivityListScrollerTasks"`
// spiegeln (dort mit Open/Mine/All-Suffix versehen, s. GenericActivityListTabPanel.vue). Kein
// zentraler Export vorhanden, daher hier dieselbe Literal-Konstante wie in TaskList.vue.
const TASK_LIST_SCROLL_BASE_IDENTIFIER = 'moduleActivityListScrollerTasks';

// Nur die Default-Instanz (Modul-Tabs laufen immer auf ihr) — `?list=`-Instanz-Tabs disposen
// bereits vollständig über useListInstanceLifecycle und laufen nie durch diesen Reset (Task 1.7 im
// Layout-Hook filtert sie über tab.query.list aus). Feldliste 1:1 aus clearAllFilters/selectTheSharedQuery
// (FilterComponentActions.ts) für 'taskList' abgeleitet, OHNE deren router.replace/refreshTheList:
// Box-/Benutzer-Filter, Datumsfilter (+ Zeitraum-Preset), gewählte Shared-Query, Such-/Hidden-Suchbegriff,
// Mehrfachauswahl, Scroll-Positionen der drei Sub-Listen, aktive Ansicht. `taskList` hat keine
// Richtungs-Filter (isDirectionRoute() schließt es aus) — kein Reset-Ziel dafür.
// NICHT angefasst: sort/listViewModeLarge (persistierte Präferenzen), Datenlisten + Counts + Pagination
// + queryStoreSubscriptions (Menü-Badges/Warmup lesen den Default-Store weiter — kein $reset/$dispose).
const resetTaskListModuleTabSettings = (): void => {
	const taskListStore = useTaskListStore();
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();

	taskListStore.setFilteredBoxesAction({ boxIdFilter: [] });
	taskListStore.setFilteredUsersAction({ filterUsers: [] });
	taskListStore.setDateFilterAction({ dateFilter: today() });
	taskListStore.setDateTimeRangeFilter({ dateTimeRangeFilter: PreconfiguredTimeRanges.TODAY });
	taskListStore.setSelectedSharedQueryAction({ selectedSharedQuery: null });

	filterStore.setSearchTerm({ module: 'taskList', searchTerm: '' });
	filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module: 'taskList' });

	genericActivityListStore.clearTheSelection({ genericListIdentifier: 'taskList' });
	genericActivityListStore.resetScrollPositions(TASK_LIST_SCROLL_BASE_IDENTIFIER);
	genericActivityListStore.setActiveView({ activeView: MODULE_LIST_TYPE.OPEN, genericListIdentifier: 'taskList' });
};

registerModuleTabSettingsReset('taskList', resetTaskListModuleTabSettings);

const deleteTheSelectedTaskAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: { aggregateIds: string[]; genericListIdentifier?: string; processId: string }
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const taskListStore: ReturnType<typeof useTaskListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useTaskListStore();

		taskListStore.deleteSelectedAggregateIds({
			aggregateIds: queryParameters.aggregateIds,
		});
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'taskList',
	});
};

const setTheStateForTheSelectedTaskAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: {
		aggregateIds: string[];
		genericListIdentifier?: string;
		state: StatusEnum;
	}
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const taskListStore: ReturnType<typeof useTaskListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useTaskListStore();

		switch (queryParameters.state) {
			case StatusEnum.UNSEEN:
				taskListStore.setTheTaskStateToUnseen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.SEEN:
				taskListStore.setTheTaskStateToSeen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.READY:
				taskListStore.setTheTaskStateToReady({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			default:
				break;
		}
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'taskList',
	});
};

export { deleteTheSelectedTaskAggregateIds, fetchTaskList, setTheStateForTheSelectedTaskAggregateIds, warmupTaskList };
